import React, { useCallback, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { useSearchParams } from "react-router-dom";

import api from "../../../services/api";

const TAB_NUMBER = 0;
const TAB_CONVERSION = 1;

const LIMIT_DESKTOP = 10;
const LIMIT_MOBILE = 5;

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB");
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-GB");
};

function EmptyState({ text }) {
  return (
    <Box sx={{ py: 5, textAlign: "center" }}>
      <Typography color="text.secondary">{text}</Typography>
    </Box>
  );
}

function LoadingState() {
  return (
    <Box
      sx={{
        py: 6,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={32} />
    </Box>
  );
}
function ChangeValue({ from, to }) {
  const fromValue = from || "-";
  const toValue = to || "-";

  const changed = fromValue !== "-" && toValue !== "-" && fromValue !== toValue;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.7,
        flexWrap: "wrap",
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: changed ? "text.secondary" : "text.primary",
          textDecoration: changed ? "line-through" : "none",
        }}
      >
        {fromValue}
      </Typography>

      <Typography component="span" variant="body2" color="text.secondary">
        →
      </Typography>

      {changed ? (
        <Chip
          size="small"
          label={toValue}
          color="warning"
          variant="filled"
          sx={{ fontWeight: 700 }}
        />
      ) : (
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          {toValue}
        </Typography>
      )}
    </Box>
  );
}
function NumberHistoryTable({ rows, isMobile }) {
  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {rows.map((item) => (
          <Paper
            key={item.id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
            }}
          >
            <Stack spacing={0.7}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography fontWeight={700}>
                  Coach No: {item.new_coach_no}
                </Typography>

                {/* <Chip
                  size="small"
                  label={`Coach No: ${item.new_coach_no}`}
                  variant="outlined"
                /> */}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.3 }}>
                  <strong>Coach Number Change:</strong>
                </Typography>

                <ChangeValue from={item.old_coach_no} to={item.new_coach_no} />
              </Box>

              <Typography variant="body2">
                <strong>Change Date:</strong>{" "}
                {formatDate(item.new_valid_from || item.created_at)}
              </Typography>

              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                <strong>Reason:</strong> {item.new_change_reason || "-"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Created by: {item.createdby || "-"} |{" "}
                {formatDateTime(item.created_at)}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ overflowX: "auto" }}
    >
      <Table size="small" sx={{ minWidth: 850 }}>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontWeight: 700,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableCell>Coach Number </TableCell>
            <TableCell>Coach Number Change</TableCell>
            <TableCell>Change Date</TableCell>
            <TableCell>Change Reason</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Created At</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>{item.new_coach_no} </TableCell>

              <TableCell>
                <ChangeValue from={item.old_coach_no} to={item.new_coach_no} />
              </TableCell>

              <TableCell>
                {formatDate(item.new_valid_from || item.created_at)}
              </TableCell>

              <TableCell>{item.new_change_reason || "-"}</TableCell>

              <TableCell>{item.createdby || "-"}</TableCell>

              <TableCell>{formatDateTime(item.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ConversionValue({ from, to, label }) {
  const fromValue = from || "-";
  const toValue = to || "-";

  const changed = fromValue !== "-" && toValue !== "-" && fromValue !== toValue;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.7,
        flexWrap: "wrap",
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: changed ? "text.secondary" : "text.primary",
          textDecoration: changed ? "line-through" : "none",
        }}
      >
        {fromValue}
      </Typography>

      <Typography component="span" variant="body2" color="text.secondary">
        →
      </Typography>

      {changed ? (
        <Chip
          size="small"
          label={toValue}
          color="warning"
          variant="filled"
          sx={{
            fontWeight: 700,
          }}
        />
      ) : (
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {toValue}
        </Typography>
      )}
    </Box>
  );
}

function ConversionHistoryTable({ rows, isMobile }) {
  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {rows.map((item) => (
          <Paper
            key={item.id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
            }}
          >
            <Stack spacing={0.7}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography fontWeight={700}>
                  Coach No: {item.coach_no}
                </Typography>

                <Chip
                  size="small"
                  label={formatDate(item.conversion_date)}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.3 }}>
                  <strong>Coach Type:</strong>
                </Typography>

                <ConversionValue
                  from={item.from_coach_type_code}
                  to={item.to_coach_type_code}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.3 }}>
                  <strong>Make:</strong>
                </Typography>

                <ConversionValue
                  from={item.from_make_code}
                  to={item.to_make_code}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.3 }}>
                  <strong>Repair Type:</strong>
                </Typography>

                <ConversionValue
                  from={item.from_repair_type_code}
                  to={item.to_repair_type_code}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.3 }}>
                  <strong>Base:</strong>
                </Typography>

                <ConversionValue
                  from={item.from_base_code}
                  to={item.to_base_code}
                />
              </Box>

              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                <strong>Reason:</strong> {item.conversion_reason || "-"}
              </Typography>

              {item.remark && (
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  <strong>Remark:</strong> {item.remark}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                Created by: {item.createdby || "-"} |{" "}
                {formatDateTime(item.created_at)}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ overflowX: "auto" }}
    >
      <Table size="small" sx={{ minWidth: 1650 }}>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontWeight: 700,
                whiteSpace: "nowrap",
              },
            }}
          >
            {/* <TableCell>Coach ID</TableCell> */}
            <TableCell>Coach No.</TableCell>

            <TableCell>Coach Type Change</TableCell>
            <TableCell>Base Change</TableCell>
            <TableCell>Make Change</TableCell>
            <TableCell>Repair Type Change</TableCell>

            <TableCell>Conversion Date</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Remark</TableCell>

            <TableCell>Created By</TableCell>
            <TableCell>Created At</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((item) => (
            <TableRow key={item.id} hover>
              {/* <TableCell>{item.coach_id}</TableCell> */}

              <TableCell sx={{ fontWeight: 600 }}>{item.coach_no}</TableCell>

              <TableCell>
                <ConversionValue
                  from={item.from_coach_type_code}
                  to={item.to_coach_type_code}
                />
              </TableCell>

              <TableCell>
                <ConversionValue
                  from={item.from_base_code}
                  to={item.to_base_code}
                />
              </TableCell>

              <TableCell>
                <ConversionValue
                  from={item.from_make_code}
                  to={item.to_make_code}
                />
              </TableCell>

              <TableCell>
                <ConversionValue
                  from={item.from_repair_type_code}
                  to={item.to_repair_type_code}
                />
              </TableCell>

              <TableCell>{formatDate(item.conversion_date)}</TableCell>

              <TableCell>{item.conversion_reason || "-"}</TableCell>

              <TableCell
                sx={{
                  maxWidth: 250,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {item.remark || "-"}
              </TableCell>

              <TableCell>{item.createdby || "-"}</TableCell>

              <TableCell>{formatDateTime(item.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function CoachHistory({
  initialCoachNo = "",
  embedded = false,
  onClose,
}) {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchParams] = useSearchParams();

const coachNoFromUrl =
  searchParams.get("coachNo") || "";

const initialSearch =
  initialCoachNo || coachNoFromUrl;

const [search, setSearch] = useState(initialSearch);

  const rowsPerPage = isMobile ? LIMIT_MOBILE : LIMIT_DESKTOP;

  const [tab, setTab] = useState(TAB_NUMBER);

  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);


const [numberTotal, setNumberTotal] = useState(0);
const [conversionTotal, setConversionTotal] = useState(0);


  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const endpoint =
        tab === TAB_NUMBER
          ? "/coach-history/number"
          : "/coach-history/conversion";

      const response = await api.get(endpoint, {
        params: {
          page,
          limit: rowsPerPage,
          search: search.trim(),
        },
      });

      const responseData = response.data;

      const historyRows = Array.isArray(responseData?.data)
        ? responseData.data
        : [];

      const pagination = responseData?.pagination || {};

      setRows(historyRows);

const recordTotal = Number(pagination.total || 0);

setRows(historyRows);
setTotal(recordTotal);

if (tab === TAB_NUMBER) {
  setNumberTotal(recordTotal);
} else {
  setConversionTotal(recordTotal);
}

setTotalPages(
  Math.max(Number(pagination.totalPages || 1), 1)
);





      setTotal(Number(pagination.total || 0));

      setTotalPages(Math.max(Number(pagination.totalPages || 1), 1));
    } catch (error) {
      console.error("loadHistory:", error);

      setRows([]);
      setTotal(0);
      setTotalPages(1);

      setError(error.response?.data?.message || "Failed to load coach history");
    } finally {
      setLoading(false);
    }
  }, [tab, page, rowsPerPage, search]);


const loadHistoryCounts = useCallback(async () => {
  try {
    const [numberResponse, conversionResponse] = await Promise.all([
      api.get("/coach-history/number", {
        params: {
          page: 1,
          limit: 1,
          search: initialSearch.trim(),
        },
      }),

      api.get("/coach-history/conversion", {
        params: {
          page: 1,
          limit: 1,
          search: initialSearch.trim(),
        },
      }),
    ]);

    setNumberTotal(
      Number(numberResponse.data?.pagination?.total || 0)
    );

    setConversionTotal(
      Number(conversionResponse.data?.pagination?.total || 0)
    );
  } catch (error) {
    console.error("loadHistoryCounts:", error);
  }
}, [initialSearch]);




useEffect(() => {
  loadHistory();
  loadHistoryCounts();
}, [loadHistory, loadHistoryCounts]);



  const handleTabChange = (_, newValue) => {
  setTab(newValue);
  setSearch(initialSearch);
  setPage(1);
  setRows([]);
};

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  return (
    <Box
  sx={{
    width: "100%",
    minWidth: 0,
    p: embedded
      ? { xs: 1, sm: 2 }
      : { xs: 1.5, sm: 2.5 },
  }}
>
      {/* Header */}
     {!embedded && (
  <Box
    sx={{
      mb: 2.5,
      display: "flex",
      alignItems: {
        xs: "flex-start",
        sm: "center",
      },
      justifyContent: "space-between",
      flexDirection: {
        xs: "column",
        sm: "row",
      },
      gap: 2,
    }}
  >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <HistoryIcon
            sx={{
              fontSize: 30,
              color: "primary.main",
            }}
          />

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Coach History
            </Typography>

            <Typography variant="body2" color="text.secondary">
              View coach number and conversion history.
            </Typography>
          </Box>
        </Box>

        <Chip label={`${total} Records`} color="warning" variant="outlined" />
      </Box>
)}
      <Card
        sx={{
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          borderRadius: 2.5,
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
            },
          }}
        >
          {/* Tabs */}
          <Tabs
  value={tab}
  onChange={handleTabChange}
  variant={isMobile ? "fullWidth" : "standard"}
  sx={{
    mb: 2,
    borderBottom: 1,
    borderColor: "divider",
  }}
>
  <Tab
    label={
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
        }}
      >
        <span>Number History</span>

        <Chip
          label={numberTotal}
          size="small"
          color="warning"
          variant="outlined"
          sx={{
            height: 22,
            minWidth: 28,
            fontSize: "0.75rem",
          }}
        />
      </Box>
    }
  />

  <Tab
    label={
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
        }}
      >
        <span>Conversion History</span>

        <Chip
          label={conversionTotal}
          size="small"
          color="warning"
          variant="outlined"
          sx={{
            height: 22,
            minWidth: 28,
            fontSize: "0.75rem",
          }}
        />
      </Box>
    }
  />
</Tabs>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={handleSearchChange}
            disabled={embedded}
            placeholder={
              tab === TAB_NUMBER
                ? "Search old/new coach no. or reason..."
                : "Search coach, type, base, make, repair type, reason..."
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Content */}
          {loading ? (
            <LoadingState />
          ) : rows.length === 0 ? (
            <EmptyState
              text={
                search.trim()
                  ? "No matching history records found."
                  : "No history records found."
              }
            />
          ) : tab === TAB_NUMBER ? (
            <NumberHistoryTable rows={rows} isMobile={isMobile} />
          ) : (
            <ConversionHistoryTable rows={rows} isMobile={isMobile} />
          )}

          {/* Pagination */}
          {!loading && total > 0 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Pagination
                count={totalPages}
                page={Math.min(page, totalPages)}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
